<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  size: {
    type: Number,
    default: 150,
  },
  isThinking: {
    type: Boolean,
    default: true,
  },
  placementClass: {
    type: String,
    default: 'left-3 bottom-4',
  },
  dismissible: {
    type: Boolean,
    default: true,
  },
  dismissStorageKey: {
    type: String,
    default: 'assistant_robot_overlay_hidden',
  },
  showBubble: {
    type: Boolean,
    default: false,
  },
  agentMessage: {
    type: String,
    default: '',
  },
  buttons: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['action']);

const isDismissed = ref(false);
const { t } = useI18n();

const isVisible = computed(() => props.show && !isDismissed.value);
const assistantName = computed(() => {
  const translated = t('CONVERSATION.AI_ROBOT_OVERLAY.TITLE');
  return translated === 'CONVERSATION.AI_ROBOT_OVERLAY.TITLE'
    ? 'Genius AI'
    : translated;
});
const messageText = computed(() => props.agentMessage || '');
const actionButtons = computed(() => props.buttons || []);

onMounted(() => {
  if (!props.dismissible || typeof window === 'undefined') return;
  isDismissed.value = localStorage.getItem(props.dismissStorageKey) === 'true';
});

const dismissOverlay = () => {
  isDismissed.value = true;
  if (typeof window === 'undefined') return;
  localStorage.setItem(props.dismissStorageKey, 'true');
};

const onAction = button => {
  emit('action', button);
};
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-500 ease-out"
    enter-from-class="-translate-x-16 opacity-0"
    enter-to-class="translate-x-0 opacity-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="translate-x-0 opacity-100"
    leave-to-class="-translate-x-10 opacity-0"
  >
    <div v-show="isVisible" class="fixed z-[80]" :class="props.placementClass">
      <div class="relative" :style="{ width: `${size + 190}px` }">
        <Transition
          enter-active-class="transition-all duration-250 ease-out delay-100"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-1"
        >
          <div
            v-if="showBubble"
            class="pointer-events-auto absolute left-[136px] -top-3 z-20 w-[220px] rounded-2xl border border-n-weak bg-n-solid-2 p-3 shadow-xl"
          >
            <div v-if="assistantName" class="mb-2 flex items-center gap-2">
              <span class="size-2 rounded-full bg-n-brand" />
              <span class="text-xs font-semibold text-n-slate-12">
                {{ assistantName }}
              </span>
            </div>
            <p
              v-if="messageText"
              class="mb-3 text-sm leading-5 text-n-slate-11"
            >
              {{ messageText }}
            </p>
            <div v-if="actionButtons.length" class="flex items-center gap-2">
              <button
                v-for="button in actionButtons"
                :key="button.id"
                type="button"
                :class="
                  button.variant === 'primary'
                    ? 'rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white hover:opacity-90'
                    : 'rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-1'
                "
                @click="onAction(button)"
              >
                {{ button.label }}
              </button>
            </div>
            <span
              class="absolute -left-1 bottom-8 size-3 rotate-45 border-b border-l border-n-weak bg-n-solid-2"
            />
          </div>
        </Transition>

        <button
          v-if="dismissible"
          type="button"
          class="absolute -left-2 -top-3 z-30 flex size-7 items-center justify-center rounded-full border-2 border-white bg-black/80 text-white shadow-lg hover:bg-black"
          :aria-label="t('CONVERSATION.AI_ROBOT_OVERLAY.CLOSE')"
          @click="dismissOverlay"
        >
          <fluent-icon icon="dismiss" size="14" />
        </button>

        <svg
          viewBox="0 0 240 290"
          :width="size"
          :height="Math.round(size * 1.22)"
          aria-hidden="true"
          class="pointer-events-none drop-shadow-[0_10px_15px_rgba(0,0,0,0.14)]"
        >
          <defs>
            <linearGradient id="headShell" x1="0%" x2="100%" y1="0%" y2="100%">
              <stop offset="0%" stop-color="#f8f9fc" />
              <stop offset="55%" stop-color="#e7eaf2" />
              <stop offset="100%" stop-color="#d4dae8" />
            </linearGradient>
            <linearGradient id="blueDeep" x1="0%" x2="100%" y1="0%" y2="100%">
              <stop offset="0%" stop-color="#2f4a9c" />
              <stop offset="55%" stop-color="#1a2f6c" />
              <stop offset="100%" stop-color="#0f1f4b" />
            </linearGradient>
            <radialGradient id="visorGlow" cx="50%" cy="22%" r="85%">
              <stop offset="0%" stop-color="#6cf4ff" stop-opacity="0.55" />
              <stop offset="35%" stop-color="#223968" stop-opacity="0.45" />
              <stop offset="100%" stop-color="#0b1530" stop-opacity="1" />
            </radialGradient>
            <linearGradient id="bodyShell" x1="0%" x2="100%" y1="0%" y2="100%">
              <stop offset="0%" stop-color="#ffffff" />
              <stop offset="65%" stop-color="#e7e9f0" />
              <stop offset="100%" stop-color="#cfd5e2" />
            </linearGradient>
            <radialGradient id="cyanShadow" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stop-color="#57efff" stop-opacity="0.7" />
              <stop offset="100%" stop-color="#57efff" stop-opacity="0" />
            </radialGradient>
            <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="3" />
            </filter>
          </defs>

          <ellipse cx="120" cy="268" rx="52" ry="16" fill="url(#cyanShadow)" />

          <g>
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0 0;0 -10;0 0"
              dur="4s"
              repeatCount="indefinite"
            />

            <line
              x1="120"
              y1="34"
              x2="120"
              y2="16"
              stroke="#28335f"
              stroke-width="4"
            />
            <circle cx="120" cy="13" r="7" fill="#ff9a1f" />
            <circle
              cx="120"
              cy="13"
              r="11"
              fill="#ff9a1f"
              opacity="0.22"
              filter="url(#softGlow)"
            />
            <ellipse cx="120" cy="34" rx="24" ry="8" fill="url(#blueDeep)" />
            <ellipse
              cx="120"
              cy="33"
              rx="13"
              ry="4"
              fill="#6cf4ff"
              opacity="0.65"
            />

            <ellipse
              cx="120"
              cy="90"
              rx="66"
              ry="55"
              fill="url(#headShell)"
              stroke="#c6ccda"
              stroke-width="2.5"
            />
            <ellipse
              cx="120"
              cy="89"
              rx="74"
              ry="62"
              fill="url(#headShell)"
              opacity="0.06"
            />
            <ellipse cx="53" cy="95" rx="10" ry="22" fill="url(#blueDeep)" />
            <ellipse cx="187" cy="95" rx="10" ry="22" fill="url(#blueDeep)" />

            <rect
              x="67"
              y="60"
              width="106"
              height="60"
              rx="24"
              fill="#18244b"
              stroke="#2f4382"
              stroke-width="2"
            />
            <rect
              x="73"
              y="66"
              width="94"
              height="48"
              rx="18"
              fill="url(#visorGlow)"
            />

            <rect x="98" y="78" width="12" height="26" rx="6" fill="#2bf4ff">
              <animate
                attributeName="height"
                :values="isThinking ? '26;26;3;26' : '26;26;26;26'"
                dur="3.6s"
                repeatCount="indefinite"
              />
            </rect>
            <rect x="130" y="78" width="12" height="26" rx="6" fill="#2bf4ff">
              <animate
                attributeName="height"
                :values="isThinking ? '26;26;3;26' : '26;26;26;26'"
                dur="3.6s"
                repeatCount="indefinite"
              />
            </rect>
            <path
              d="M113 108 C117 114, 123 114, 127 108"
              stroke="#2bf4ff"
              stroke-width="4"
              fill="none"
              stroke-linecap="round"
            />

            <ellipse cx="120" cy="144" rx="16" ry="6" fill="url(#blueDeep)" />
            <ellipse
              cx="120"
              cy="143"
              rx="8"
              ry="3"
              fill="#6cf4ff"
              opacity="0.55"
            />

            <ellipse
              cx="120"
              cy="207"
              rx="44"
              ry="52"
              fill="url(#bodyShell)"
              stroke="#c6ccda"
              stroke-width="2.5"
            />
            <path
              d="M84 229 Q120 215 156 229"
              fill="none"
              stroke="#c6ccd8"
              stroke-width="2"
            />
            <ellipse
              cx="94"
              cy="185"
              rx="12"
              ry="20"
              fill="#ffffff"
              opacity="0.35"
            />

            <ellipse cx="86" cy="181" rx="9" ry="13" fill="url(#blueDeep)" />
            <ellipse cx="154" cy="181" rx="9" ry="13" fill="url(#blueDeep)" />
            <ellipse
              cx="72"
              cy="201"
              rx="13"
              ry="32"
              fill="url(#bodyShell)"
              stroke="#c6ccda"
              stroke-width="2"
              transform="rotate(10 72 201)"
            />
            <ellipse
              cx="168"
              cy="201"
              rx="13"
              ry="32"
              fill="url(#bodyShell)"
              stroke="#c6ccda"
              stroke-width="2"
              transform="rotate(-10 168 201)"
            />
            <circle cx="69" cy="216" r="6.4" fill="#4cff7a" />
            <circle cx="171" cy="216" r="6.4" fill="#4cff7a" />
            <circle cx="69" cy="216" r="10" fill="#4cff7a" opacity="0.25" />
            <circle cx="171" cy="216" r="10" fill="#4cff7a" opacity="0.25" />

            <rect
              x="58"
              y="227"
              width="8"
              height="18"
              rx="4"
              fill="url(#blueDeep)"
            />
            <rect x="66" y="230" width="8" height="18" rx="4" fill="#e5e9f2" />
            <rect
              x="174"
              y="227"
              width="8"
              height="18"
              rx="4"
              fill="url(#blueDeep)"
            />
            <rect x="166" y="230" width="8" height="18" rx="4" fill="#e5e9f2" />
          </g>
        </svg>
      </div>
    </div>
  </Transition>
</template>
